// ─── Role → module/route access matrix ─────────────────────────────────────────
//
// Single source of truth for which GlobalRoles may see each sidebar module and
// reach each route. Derived from the product "Dxtr Role Access" matrix.
//
// Consumed by:
//   - components/erp/sidebar.tsx   → filters the visible nav
//   - components/erp/erp-layout.tsx → guards routes (403 on no-access)
//
// Notes:
//   - "QC" in the source matrix maps to the `qa_*` roles in code.
//   - system_admin is platform-only (Firms). read_only currently has no access.
//   - Routes not covered by any rule are allowed by default (out of matrix scope).

import { primaryGlobalRole, type GlobalRole } from "@/lib/api/iam"
import { DASHBOARD_FEATURES, OFFERINGS, hasAnyFeature } from "@/lib/access/offerings"

export interface AccessRule {
  /** Stable module key, linked to nav items in the sidebar. */
  key: string
  /** Route prefix this rule governs. Longest matching prefix wins. */
  path: string
  /** Global roles permitted to access this module/route. */
  roles: GlobalRole[]
}

// Convenience role bundles.
const ALL_TENANT: GlobalRole[] = [
  "tenant_admin",
  "project_manager",
  "qa_manager",
  "qa_reviewer",
  "scientist",
]
const RND: GlobalRole[] = ["tenant_admin", "project_manager", "scientist"]
/**
 * Every role that can hold a session. Use ONLY for surfaces that belong to the
 * person rather than the organisation - `ALL_TENANT` deliberately omits
 * plant_manager, operator and read_only, and that omission must not follow a
 * user into their own profile.
 */
const EVERY_ROLE: GlobalRole[] = [
  "system_admin",
  "tenant_admin",
  "project_manager",
  "qa_manager",
  "qa_reviewer",
  "scientist",
  "plant_manager",
  "operator",
  "data_entry",
  "read_only",
]

export const ACCESS_RULES: AccessRule[] = [
  // qa_reviewer is intentionally excluded - a QC reviewer is an operational role
  // that lands on and works from the /qaqc worklist; the dashboard (analytical,
  // manager-oriented) is hidden from them in the sidebar AND 403-guarded.
  // plant_manager: read-only plant-floor oversight - granted exactly the seven
  // enumerated read surfaces (dashboard, samples, qaqc, eln/experiments,
  // instruments, methods, projects). Deliberately NOT added to the ALL_TENANT /
  // RND bundles so non-listed surfaces (sops, products, settings, suppliers,
  // raw-materials, reports) stay fails-closed for this read-only role.
  // operator: plant-execution persona - DELIBERATELY narrower than plant_manager.
  // Granted only the plant-execution surfaces that exist today (mbr recipe
  // library + equipment, both read-only, and the batch-sheet runtime under
  // /batches). No R&D / QC / analytics / dashboard access. Everything else fails
  // closed.
  // data_entry: front-office record keeping - granted exactly requests,
  // customers and suppliers (plus their own /account), added PER RULE and never
  // through a bundle (DEC-0099: a bundle-add would over-grant every surface
  // that uses it). Everything else fails closed.
  { key: "dashboard",     path: "/dashboard",                roles: ["tenant_admin", "project_manager", "qa_manager", "scientist", "plant_manager"] },
  // founder-cockpit D1: the pre-cockpit tenant-admin dashboard fork is preserved
  // verbatim at /dashboard_old (working fallback). UX-only rule - mirrors the
  // `dashboard` roles; the authorization control for the cockpit endpoints is the
  // backend `IsCockpitReviewer` gate. Longest-prefix match keeps it distinct from
  // `/dashboard`.
  { key: "dashboard_old", path: "/dashboard_old",            roles: ["tenant_admin", "project_manager", "qa_manager", "scientist", "plant_manager"] },
  { key: "customers",     path: "/customers",                roles: ["tenant_admin", "data_entry"] },
  { key: "pr",            path: "/rnd/pr1",                  roles: RND },
  { key: "insilico",      path: "/rnd/insi",                 roles: RND },
  { key: "formulations",  path: "/rnd/eln/formulations", roles: [...RND, "plant_manager"] },
  { key: "notebooks",     path: "/rnd/eln",              roles: [...RND, "plant_manager"] },
  // customer-request-spine (G1): the pre-project enquiry inbox. Sits ABOVE
  // Projects in the nav, per the approved design. ALL_TENANT plus data_entry
  // (data-entry-role: logging enquiries is that role's job) —
  // plant_manager / operator / read_only stay fails-closed here, and
  // system_admin is deliberately absent (it is org-less, so the API returns
  // zero rows for it by construction). Per-record grants gate EDIT at the API,
  // not visibility, so there is no record-level read gate to mirror here.
  { key: "requests",      path: "/requests",                 roles: [...ALL_TENANT, "data_entry"] },
  { key: "projects",      path: "/rnd",                      roles: [...ALL_TENANT, "plant_manager"] },
  // sop-access-control: route gate widened to ALL_TENANT - every tenant role
  // reaches /sop-library; the members-only access decision is now at the API
  // record level (members-only get_queryset), not the route.
  { key: "sops",          path: "/sop-library",              roles: ALL_TENANT },
  // MBR (Master Batch Record) library - process chemist (scientist) + QA author/
  // approve; plant_manager gets read-only oversight. Members-only access is
  // enforced at the API record level (fails-closed get_queryset), not the route.
  { key: "mbr",           path: "/mbr-library",              roles: [...ALL_TENANT, "plant_manager", "operator"] },
  { key: "samples",       path: "/samples",                  roles: [...ALL_TENANT, "plant_manager"] },
  { key: "qaqc",          path: "/qaqc",                     roles: ["tenant_admin", "project_manager", "qa_manager", "qa_reviewer", "plant_manager"] },
  { key: "instruments",   path: "/instruments",              roles: ["tenant_admin", "project_manager", "qa_manager", "scientist", "plant_manager"] },
  // Plant Equipment Register - write = registry-admin trio (system_admin is
  // platform-only, omitted per the convention used by every other tenant rule);
  // qa_reviewer writes the cleaning/calibration records on the detail page;
  // plant_manager gets read-only plant-floor oversight (no create/edit UI).
  { key: "equipment",     path: "/equipment",                roles: ["tenant_admin", "project_manager", "qa_reviewer", "plant_manager", "operator"] },
  // Batch module. operator gains EXECUTE-WRITE on the batch-sheet runtime
  // (batch-sheet §7) - the write affordances are gated by the same role set the
  // DRF viewset enforces. plant_manager + QA reach the detail for read +
  // review-by-exception (acknowledge-excursion); tenant_admin keeps oversight.
  // Batch CREATE authority stays separate (plant_manager + admins) - operator
  // still cannot create a batch.
  { key: "batches",       path: "/batches",                  roles: ["operator", "plant_manager", "tenant_admin", "qa_manager", "qa_reviewer"] },
  { key: "methods",       path: "/methods",                  roles: [...ALL_TENANT, "plant_manager"] },
  { key: "suppliers",     path: "/suppliers",                roles: ["tenant_admin", "project_manager", "qa_manager", "qa_reviewer", "data_entry"] },
  { key: "raw-materials", path: "/raw-materials",            roles: ["tenant_admin", "project_manager", "qa_manager", "qa_reviewer"] },
  { key: "products",      path: "/products",                 roles: ALL_TENANT },
  { key: "reports",       path: "/reports",                  roles: ["tenant_admin", "project_manager", "qa_manager"] },
  { key: "assistant",     path: "/assistant",                roles: ["tenant_admin", "project_manager", "qa_manager", "scientist", "plant_manager", "qa_reviewer"] },
  { key: "settings",      path: "/settings",                 roles: ALL_TENANT },
  // The guided Tally setup under Settings. Organisation configuration, so
  // tenant_admin only - the same role the Integrations tab and the BE
  // (`tally/permissions.py`) enforce. The `tally` OFFERING gate is not a
  // route rule (the offering claims no route); the page checks it itself.
  { key: "settings-integrations", path: "/settings/integrations", roles: ["tenant_admin"] },
  // Your own profile, split out of /settings so it is not gated behind
  // organisation configuration. While it was a Settings tab, the three roles
  // outside ALL_TENANT (plant_manager, operator, read_only) could not reach
  // their own name or email at all. Reached from the sidebar profile block, not
  // the Settings gear, so it has no nav entry of its own.
  { key: "account",       path: "/account",                  roles: EVERY_ROLE },
  // Covers every /firms/** surface, including the nested ones with no sidebar
  // entry of their own: /firms/[id], /firms/[id]/users, and /firms/[id]/import
  // (onboarding source import - loads a firm's approved-vendor list from one
  // combined CSV). All four say the same thing - system_admin only, which is
  // exactly what the backend enforces (the import endpoints refuse every other
  // role, tenant_admin included). A separate rule per nested route would only
  // repeat this one; the import previously had one pinned to the old static
  // /firms/import path, which stopped matching when the route moved under the
  // firm id and became protection that was not there.
  { key: "firms",         path: "/firms",                    roles: ["system_admin"] },
]

// ─── Per-role sidebar layout ────────────────────────────────────────────────
//
// Access (above) decides WHICH modules a role MAY see. Layout (here) decides,
// for a given role, exactly which of those modules appear in the sidebar and in
// what ORDER. A role listed here gets a curated sidebar: ONLY the listed module
// keys, in this exact sequence - everything else is hidden even if the role has
// route access to it. Roles NOT listed fall back to the default nav order
// (every module they can access, in ACCESS_RULES/nav declaration order).
//
// Resolved by the user's PRIMARY (highest-priority) role, so a curated layout
// only applies when its role is the dominant one - e.g. a tenant_admin who also
// holds plant_manager still gets the full admin nav, not the curated plant view.
//
// To curate another role's sidebar, add an entry keyed by its module keys.
export const ROLE_NAV_LAYOUT: Partial<Record<GlobalRole, string[]>> = {
  plant_manager: [
    "dashboard",
    "batches",
    "mbr",
    "equipment",
    "samples",
    "qaqc",
    "formulations",
  ],
  // operator: batch-execution persona. Sidebar is curated down to the two
  // surfaces they act on - Batches (batch-sheet runtime) and Equipment. MBR
  // stays route-accessible (read-only) so a deep-link from a batch into its
  // source recipe still resolves, but it is intentionally hidden from the nav.
  operator: [
    "batches",
    "equipment",
  ],
  // data_entry: front-office record keeping - the three surfaces it writes.
  data_entry: [
    "requests",
    "customers",
    "suppliers",
  ],
}

/**
 * The curated sidebar layout (ordered module keys) for a user, or null if their
 * primary role has no curated layout (caller should use the default nav order).
 */
export function navLayoutForRoles(globalRoles: GlobalRole[]): string[] | null {
  const primary = primaryGlobalRole(globalRoles)
  if (!primary) return null
  return ROLE_NAV_LAYOUT[primary] ?? null
}

const RULES_BY_KEY = new Map(ACCESS_RULES.map((r) => [r.key, r]))

/** Finds the most specific (longest-prefix) rule governing a pathname. */
function matchRule(pathname: string): AccessRule | null {
  let best: AccessRule | null = null
  for (const rule of ACCESS_RULES) {
    const isMatch = pathname === rule.path || pathname.startsWith(rule.path + "/")
    if (isMatch && (!best || rule.path.length > best.path.length)) best = rule
  }
  return best
}

/**
 * True if the user's global roles permit access to the given route.
 * Routes not covered by any rule are allowed (out of matrix scope).
 */
export function canAccessRoute(pathname: string, globalRoles: GlobalRole[]): boolean {
  const rule = matchRule(pathname)
  if (!rule) return true
  return rule.roles.some((r) => globalRoles.includes(r))
}

/** True if the user's global roles permit a specific module (by key). */
export function canAccessModuleKey(key: string, globalRoles: GlobalRole[]): boolean {
  const rule = RULES_BY_KEY.get(key)
  if (!rule) return false
  return rule.roles.some((r) => globalRoles.includes(r))
}

// ─── Offering (feature) route gate ──────────────────────────────────────────
//
// The SECOND, orthogonal route gate: "has the firm bought the offering this
// route belongs to?" Independent of the role gate above and ANDed with it in
// ERPLayout. Derived from `OFFERINGS` (lib/access/offerings.ts) so the
// offering→module mapping stays single-source - shared modules (Instruments,
// Methods) accumulate every offering key that lists them, giving any-of
// (`eln` OR `qc`) gating for free. Routes owned by no offering (always-on /
// all-tenant) are never feature-gated.
//
// NOTE: this is FRONTEND visibility only - the backend does not (yet) enforce
// `enabled_features` on its endpoints, so a determined caller can still hit the
// API directly. See goals/module-entitlements - BE enforcement is deferred.

interface FeatureRoute {
  path: string
  keys: string[]
}

const FEATURE_ROUTES: FeatureRoute[] = (() => {
  const byRoute = new Map<string, Set<string>>()
  for (const offering of OFFERINGS) {
    for (const mod of offering.modules) {
      const set = byRoute.get(mod.route) ?? new Set<string>()
      set.add(offering.key)
      byRoute.set(mod.route, set)
    }
  }
  // Routes no offering owns but which still need one: the dashboard (and its
  // preserved fork) need ANY of eln / qc / production - see DASHBOARD_FEATURES.
  for (const path of ["/dashboard", "/dashboard_old"]) {
    byRoute.set(path, new Set<string>(DASHBOARD_FEATURES))
  }
  return [...byRoute.entries()].map(([path, keys]) => ({ path, keys: [...keys] }))
})()

/** Most specific (longest-prefix) offering rule governing a pathname, or null. */
function matchFeatureRoute(pathname: string): FeatureRoute | null {
  let best: FeatureRoute | null = null
  for (const rule of FEATURE_ROUTES) {
    const isMatch = pathname === rule.path || pathname.startsWith(rule.path + "/")
    if (isMatch && (!best || rule.path.length > best.path.length)) best = rule
  }
  return best
}

/**
 * True if the firm's enabled offerings permit this route. Routes owned by no
 * offering (always-on / all-tenant, e.g. /rnd, /sop-library, /raw-materials) are
 * always allowed. Shared modules pass on any-of (`eln` OR `qc`). Covers detail
 * routes via longest-prefix match (e.g. /batches/123 → /batches → `production`).
 */
export function canAccessRouteByFeature(pathname: string, enabledFeatures: string[]): boolean {
  const rule = matchFeatureRoute(pathname)
  if (!rule) return true
  return hasAnyFeature(enabledFeatures, rule.keys)
}

/** Generic default landing route when no role-specific home applies. */
export const DEFAULT_LANDING_ROUTE = "/rnd/eln"

/**
 * The landing route a role would choose, ignoring what the firm has bought.
 * system_admin lands on Firms; a qa_manager lands on the QC dashboard
 * (control-tower overview, worklist one click away); a qa_reviewer (operational)
 * lands on the QA/QC worklist; everyone else gets the generic default.
 */
function roleHome(globalRoles: GlobalRole[]): string {
  switch (primaryGlobalRole(globalRoles)) {
    case "system_admin":
      return "/firms"
    case "tenant_admin":
      return "/dashboard"
    case "qa_manager":
      return "/dashboard"
    case "qa_reviewer":
      return "/qaqc"
    case "plant_manager":
      // TODO: repoint to Plant Command Center when it ships
      return "/dashboard"
    case "operator":
      // Operator's home is the batch module - their batch-sheet execution surface.
      return "/batches"
    case "data_entry":
      // Data Entry's job is logging enquiries - home is the request inbox.
      return "/requests"
    default:
      return DEFAULT_LANDING_ROUTE
  }
}

/**
 * Where to send this user after login, and the "No access" panel's way out.
 *
 * The role's home if the user can open it - by role AND by the firm's
 * offerings - otherwise the first of /requests, /rnd they can open. Without
 * the offering check a firm with no ELN sent every scientist to /rnd/eln, and
 * a firm with none of ELN / QC / Production sent its admins to a dashboard
 * it cannot show: both log in onto "No access" (owner, 2026-10-09).
 *
 * If nothing qualifies the role's home is returned unchanged, so a role with
 * no reachable surface (read_only; an operator in a firm without Production)
 * behaves exactly as before.
 *
 * `enabledFeatures` is required, not optional: a caller that forgot it would
 * silently land every firm as though it had bought nothing.
 */
export function landingRoute(
  globalRoles: GlobalRole[],
  enabledFeatures: string[] | undefined,
): string {
  const features = enabledFeatures ?? []
  const home = roleHome(globalRoles)
  const reachable = (path: string) =>
    canAccessRoute(path, globalRoles) && canAccessRouteByFeature(path, features)
  for (const path of [home, "/requests", "/rnd"]) {
    if (reachable(path)) return path
  }
  return home
}

/**
 * Where a per-page guard (FeatureGuard, RoleGuard, a retired route) sends a
 * user it turns away. Used to be a hard-coded "/dashboard", which a firm with
 * none of ELN / QC / Production can no longer open - so the bounce landed on a
 * second refusal. Now the user's landing route; if that is the very page being
 * refused (a role with nothing reachable), their own /account, which every
 * role can open, so the guard can never redirect to itself.
 */
export function guardRedirectTarget(
  pathname: string,
  globalRoles: GlobalRole[],
  enabledFeatures: string[] | undefined,
): string {
  const target = landingRoute(globalRoles, enabledFeatures)
  return target === pathname ? "/account" : target
}

/**
 * Where the retired /reports route sends a user. Its audit trail lives in the
 * cockpit at /dashboard, so it goes there when BOTH the user's role and the
 * firm's offerings allow it, and to the user's landing route otherwise.
 */
export function reportsRedirectTarget(
  globalRoles: GlobalRole[],
  enabledFeatures: string[] | undefined,
): string {
  return canAccessRoute("/dashboard", globalRoles) &&
    canAccessRouteByFeature("/dashboard", enabledFeatures ?? [])
    ? "/dashboard"
    : landingRoute(globalRoles, enabledFeatures)
}

/**
 * Where to go after a request is converted, or null to stay on the request.
 * A user who cannot reach Projects (e.g. Data Entry) must not be sent into a
 * "No access" panel - they stay on the request and see a confirmation instead.
 */
export function postConvertDestination(
  projectId: string | null,
  globalRoles: GlobalRole[],
): string | null {
  if (!projectId) return null
  if (!canAccessModuleKey("projects", globalRoles)) return null
  return `/rnd?research=${projectId}&tab=brief`
}
